import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';
import { IUpdateAgentAIConfigurationPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { Agent } from '@/modules/gpt-chats/types/agent.service.type';
type UseAgentSubmitOptions = {
  agentId: string;
  data?: { agent: Agent };
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  mutateAsync: (
    payload: IUpdateAgentAIConfigurationPayload
  ) => Promise<{ is_success: boolean; detail?: unknown }>;
  updateWidget: (payload: {
    agent_id: string;
    regen_widget_id: boolean;
    widget: unknown;
  }) => Promise<{ is_success: boolean; detail?: unknown }>;
};

const mapGuardrailToBackend = (gr: AgentBehaviorSettingsForm['guardrail']) => ({
  enable_pii_detection: gr.enable_pii_detection,
  enable_injection_detection: gr.enable_injection_detection,
  enable_output_safety: gr.enable_output_safety,
  enable_keyword_check: gr.enable_keyword_check,
  enable_custom_rules: gr.enable_custom_rules,

  enable_pre_validation: gr.enable_pre_validation,
  enable_post_validation: gr.enable_post_validation,

  banned_keywords: gr.banned_keywords,
  banned_patterns: gr.banned_patterns,

  input_risk_threshold: gr.input_risk_threshold,
  output_risk_threshold: gr.output_risk_threshold,

  custom_rules: gr.custom_rules.map((r: any) => ({
    name: r.name,
    pattern: r.pattern,
    severity: r.severity,
    auto_block: r.auto_block,
    enable_pre: r.enable_pre,
    enable_post: r.enable_post,
    message: r.message ?? '',
  })),
});

export const useAgentSubmit = ({
  agentId,
  data,
  mutateAsync,
  updateWidget,
}: UseAgentSubmitOptions) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  const onSubmit = useCallback(
    async (values: AgentBehaviorSettingsForm) => {
      setIsSaving(true);
      try {
        const currentData = data;
        if (!currentData) return showErrorToast({ errors: 'Something went wrong' });

        const widgetPayload = {
          ...currentData.agent.widget,
          ...values.widget,
          predefined_questions: values.widget.predefined_questions.filter(Boolean),
        };

        // Remove legacy property to ensure memory_config takes precedence
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (widgetPayload as any).enable_questions_suggestions;

        const widgetRes = await updateWidget({
          agent_id: agentId,
          regen_widget_id: false,
          widget: widgetPayload,
        });
        if (!widgetRes.is_success)
          return showErrorToast({ errors: ErrorTransformer(widgetRes.detail) });

        const updatePayload = {
          ...values,
          agent_id: agentId,
          // project_key: currentTenantId ?? '',
          enable_rag: values.enable_rag,
          llm_config: {
            ...values.llm_config,
            model_id: (values.llm_config?.model_id as string) || '',
            base_prompt: (values.llm_config?.base_prompt as string) || '',
          },
          rag_config: {
            ...values.rag_config,
            embedding_model_provider: 'azure',
          },
          guardrail: mapGuardrailToBackend(values.guardrail),
          widget: widgetPayload,
        } as IUpdateAgentAIConfigurationPayload;

        const res = await mutateAsync(updatePayload);
        if (!res.is_success) return showErrorToast({ errors: ErrorTransformer(res.detail) });

        showSuccessToast({ description: 'Agent successfully updated.' });
        setLastSaveTime(new Date());
      } catch (error) {
        return showErrorToast({ errors: ErrorTransformer(error) });
      } finally {
        setIsSaving(false);
      }
    },
    [data, agentId, mutateAsync, updateWidget]
  );

  return { onSubmit, isSaving, lastSaveTime };
};
